<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import { useDrillDown } from '@/composables/useDrillDown';
import {
  ArrowLeftOutlined,
  HomeOutlined,
} from '@ant-design/icons-vue';
import CollectionsGrid from './CollectionsGrid.vue';
import FramesGrid from './FramesGrid.vue';
import OptionsGrid from './OptionsGrid.vue';
import OptionEditor from '../tree/OptionEditor.vue';
import TemplateFrameOptionDisplays
  from '../TemplateFrameOptionDisplays.vue';

const b = bem('drill-down-view');

export default {
  components: {
    ArrowLeftOutlined,
    HomeOutlined,
    CollectionsGrid,
    FramesGrid,
    OptionsGrid,
    OptionEditor,
    TemplateFrameOptionDisplays,
  },
  setup() {
    const drillDown = useDrillDown();
    return { drillDown };
  },
  computed: {
    ...mapState({
      materials: (state) => state.createTemplate.materials,
      frames: (state) => state.createTemplate.frames,
      frameOptionDisplays:
        (state) => state.createTemplate.frameOptionDisplays,
    }),
    currentType() {
      return this.drillDown.currentType.value;
    },
    currentLevel() {
      return this.drillDown.currentLevel.value;
    },
    breadcrumbs() {
      return this.drillDown.breadcrumbs.value;
    },
    // Collections for the root grid
    collections() {
      return this.materials || [];
    },
    // Frames filtered by selected collection
    collectionFrames() {
      const collectionId = this.currentLevel?.id;
      if (!collectionId) return [];
      return (this.frames || []).filter(
        (f) => f.frameMaterialId === collectionId
          && !f.isDeleted
          && !f.productCollectionId,
      );
    },
    // Options for the selected frame (from cache or store)
    frameOptions() {
      const frameId = this.currentLevel?.id;
      if (!frameId) return [];
      return this.drillDown.getCachedOptions(frameId);
    },
    // Options grouped by size for OptionsGrid
    groupedOptions() {
      const groups = {};
      this.frameOptions.forEach((opt) => {
        const sizeId = opt.frameSize?.id;
        if (!sizeId) return;
        if (!groups[sizeId]) {
          groups[sizeId] = {
            sizeId,
            sizeName: opt.frameSize.name,
            options: [],
          };
        }
        groups[sizeId].options.push(opt);
      });
      return Object.values(groups);
    },
    // For display editor — the parent option data
    selectedOption() {
      if (this.currentType === 'display') {
        return this.currentLevel?.data?.option || {};
      }
      return this.currentLevel?.data || {};
    },
    // For display editor — which display to pre-select
    initialDisplayId() {
      if (this.currentType === 'display') {
        return this.currentLevel?.data?.display?.id || null;
      }
      return null;
    },
    canGoBack() {
      return this.drillDown.stack.value.length > 1;
    },
  },
  methods: {
    ...mapActions({
      getCollectionsAction:
        'createTemplate/getTemplateCollections',
      getFramesAction:
        'createTemplate/getTemplateCollectionsFrames',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    onBreadcrumbClick(index) {
      this.drillDown.navigateTo(index);
    },
    onGoBack() {
      this.drillDown.goBack();
    },
    // Card click handlers — drill into next level
    onCollectionClick(collection) {
      this.drillDown.openCollection(collection);
    },
    async onFrameClick(frame) {
      await this.drillDown.openFrame(frame);
    },
    async onOptionClick(option) {
      await this.drillDown.openOption(option);
    },
    onDisplayClick(displayId) {
      const display = (this.frameOptionDisplays || [])
        .find((d) => d.id === displayId);
      if (display) {
        this.drillDown.openDisplay(
          display,
          this.currentLevel?.data,
        );
      }
    },
    // CRUD callbacks
    async onEntityUpdated() {
      await this.drillDown.refreshCurrentLevel();
    },
    // Stub for display editor's "go to options" button
    goToOptions() {
      this.drillDown.goBack();
    },
  },
  async mounted() {
    await Promise.all([
      this.getCollectionsAction(),
      this.getFramesAction(),
    ]);
  },
};
</script>

<template>
  <div :class="b()">
    <!-- Navigation bar: back button + breadcrumbs -->
    <div :class="b('nav')">
      <a-button
        v-if="canGoBack"
        :class="b('back-btn')"
        type="text"
        size="small"
        @click="onGoBack"
      >
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>

      <a-breadcrumb :class="b('breadcrumbs')">
        <a-breadcrumb-item
          v-for="(crumb, idx) in breadcrumbs"
          :key="idx"
        >
          <a
            v-if="idx < breadcrumbs.length - 1"
            href="#"
            @click.prevent="onBreadcrumbClick(idx)"
            @keydown.enter.prevent="onBreadcrumbClick(idx)"
          >
            <HomeOutlined v-if="idx === 0" />
            {{ crumb.label }}
          </a>
          <span v-else>{{ crumb.label }}</span>
        </a-breadcrumb-item>
      </a-breadcrumb>
    </div>

    <!-- Content area — switches based on current level type -->
    <div :class="b('content')">
      <!-- Level 1: Collections grid -->
      <CollectionsGrid
        v-if="currentType === 'collections'"
        :collections="collections"
        @select="onCollectionClick"
        @updated="onEntityUpdated"
      />

      <!-- Level 2: Frames grid for selected collection -->
      <FramesGrid
        v-if="currentType === 'frames'"
        :frames="collectionFrames"
        :collection="currentLevel.data"
        @select="onFrameClick"
        @updated="onEntityUpdated"
      />

      <!-- Level 3: Options grouped by size -->
      <OptionsGrid
        v-if="currentType === 'options'"
        :groups="groupedOptions"
        :frame="currentLevel.data"
        @select="onOptionClick"
        @updated="onEntityUpdated"
      />

      <!-- Level 4: Option detail + displays -->
      <OptionEditor
        v-if="currentType === 'optionDetail'"
        :option="currentLevel.data"
        @open-display="onDisplayClick"
        @updated="onEntityUpdated"
      />

      <!-- Level 5: Display visual constructor -->
      <TemplateFrameOptionDisplays
        v-if="currentType === 'display'"
        :selected-option="selectedOption"
        :initial-display-id="initialDisplayId"
        :toggle-option-displays="goToOptions"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.drill-down-view {
  &__nav {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 24px;
    border-bottom: 1px solid var(--Border-Light);
    background: var(--Background-Secondary);
    border-radius: 12px 12px 0 0;
  }

  &__back-btn {
    flex-shrink: 0;
  }

  &__breadcrumbs {
    a {
      color: var(--Button-Primary);
      &:hover { text-decoration: underline; }
    }
  }

  &__content {
    padding: 24px;
    min-height: 400px;

    @include media($small) {
      padding: 16px;
    }
  }
}
</style>
